import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Home, 
  Eye, 
  EyeOff, 
  Camera, 
  User, 
  ChevronRight, 
  ChevronLeft,
  CheckCircle2,
  MapPin,
  Briefcase,
  Users,
  UserPlus
} from "lucide-react";
import axios from "axios";

export default function RegisterPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Account info
  const [accountData, setAccountData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    full_name: "",
    telephone: "",
    email: ""
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Application form data
  const [applicationData, setApplicationData] = useState({
    age: "",
    gender: "",
    mobile_phone: "",
    whatsapp_phone: "",
    marital_status: "",
    marital_status_other: "",
    national_id: "",
    driver_license: "",
    last_address_1: "",
    last_address_2: "",
    last_tenant_address: "",
    duration_at_last_location: "",
    reason_for_leaving: "",
    past_landlord_phone: "",
    can_landlord_be_contacted: "",
    occupation: "",
    occupation_other: "",
    employment_duration: "",
    monthly_salary: "",
    self_employed_returns: "",
    additional_occupants: "",
    rental_duration: "",
    guarantor_1_name: "",
    guarantor_1_phone: "",
    guarantor_1_address: "",
    guarantor_1_can_contact: "",
    guarantor_2_name: "",
    guarantor_2_phone: "",
    guarantor_2_address: "",
    guarantor_2_can_contact: "",
  });

  const handleAccountChange = (e) => {
    setAccountData({ ...accountData, [e.target.name]: e.target.value });
  };

  const handleAppChange = (field, value) => {
    setApplicationData({ ...applicationData, [field]: value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: // Account info
        if (!accountData.username || !accountData.password || !accountData.full_name || !accountData.telephone) {
          toast.error("Please fill in all required fields");
          return false;
        }
        if (!profilePhoto) {
          toast.error("Profile photo is required");
          return false;
        }
        if (accountData.password !== accountData.confirmPassword) {
          toast.error("Passwords do not match");
          return false;
        }
        if (accountData.password.length < 6) {
          toast.error("Password must be at least 6 characters");
          return false;
        }
        return true;
      case 2: // Personal info
        if (!applicationData.age || !applicationData.gender || !applicationData.marital_status || !applicationData.national_id) {
          toast.error("Please fill in all required fields");
          return false;
        }
        if (!applicationData.mobile_phone && !applicationData.whatsapp_phone) {
          toast.error("Please provide at least one contact number (Mobile or WhatsApp)");
          return false;
        }
        return true;
      case 3: // Residency
        return true; // Optional fields
      case 4: // Employment
        if (!applicationData.occupation || !applicationData.additional_occupants || !applicationData.rental_duration) {
          toast.error("Please fill in all required fields");
          return false;
        }
        return true;
      case 5: // Guarantors
        if (!applicationData.guarantor_1_name || !applicationData.guarantor_1_phone || 
            !applicationData.guarantor_1_address || !applicationData.guarantor_1_can_contact ||
            !applicationData.guarantor_2_name || !applicationData.guarantor_2_phone || 
            !applicationData.guarantor_2_address || !applicationData.guarantor_2_can_contact) {
          toast.error("Please fill in all guarantor information");
          return false;
        }
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Account data
      submitData.append("username", accountData.username);
      submitData.append("password", accountData.password);
      submitData.append("full_name", accountData.full_name);
      submitData.append("telephone", accountData.telephone);
      if (accountData.email) {
        submitData.append("email", accountData.email);
      }
      submitData.append("profile_photo", profilePhoto);

      // Application data
      submitData.append("age", applicationData.age);
      submitData.append("gender", applicationData.gender);
      if (applicationData.mobile_phone) submitData.append("mobile_phone", applicationData.mobile_phone);
      if (applicationData.whatsapp_phone) submitData.append("whatsapp_phone", applicationData.whatsapp_phone);
      submitData.append("marital_status", applicationData.marital_status);
      if (applicationData.marital_status_other) submitData.append("marital_status_other", applicationData.marital_status_other);
      submitData.append("national_id", applicationData.national_id);
      if (applicationData.driver_license) submitData.append("driver_license", applicationData.driver_license);
      if (applicationData.last_address_1) submitData.append("last_address_1", applicationData.last_address_1);
      if (applicationData.last_address_2) submitData.append("last_address_2", applicationData.last_address_2);
      if (applicationData.last_tenant_address) submitData.append("last_tenant_address", applicationData.last_tenant_address);
      if (applicationData.duration_at_last_location) submitData.append("duration_at_last_location", applicationData.duration_at_last_location);
      if (applicationData.reason_for_leaving) submitData.append("reason_for_leaving", applicationData.reason_for_leaving);
      if (applicationData.past_landlord_phone) submitData.append("past_landlord_phone", applicationData.past_landlord_phone);
      if (applicationData.can_landlord_be_contacted) submitData.append("can_landlord_be_contacted", applicationData.can_landlord_be_contacted);
      submitData.append("occupation", applicationData.occupation);
      if (applicationData.occupation_other) submitData.append("occupation_other", applicationData.occupation_other);
      if (applicationData.employment_duration) submitData.append("employment_duration", applicationData.employment_duration);
      if (applicationData.monthly_salary) submitData.append("monthly_salary", applicationData.monthly_salary);
      if (applicationData.self_employed_returns) submitData.append("self_employed_returns", applicationData.self_employed_returns);
      submitData.append("additional_occupants", applicationData.additional_occupants);
      submitData.append("rental_duration", applicationData.rental_duration);
      
      // Guarantors
      submitData.append("guarantor_1_name", applicationData.guarantor_1_name);
      submitData.append("guarantor_1_phone", applicationData.guarantor_1_phone);
      submitData.append("guarantor_1_address", applicationData.guarantor_1_address);
      submitData.append("guarantor_1_can_contact", applicationData.guarantor_1_can_contact);
      submitData.append("guarantor_2_name", applicationData.guarantor_2_name);
      submitData.append("guarantor_2_phone", applicationData.guarantor_2_phone);
      submitData.append("guarantor_2_address", applicationData.guarantor_2_address);
      submitData.append("guarantor_2_can_contact", applicationData.guarantor_2_can_contact);

      await axios.post(`${API}/auth/register-with-application`, submitData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setSubmitted(true);
      toast.success("Registration and application submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: "Account", icon: UserPlus },
    { num: 2, title: "Personal Info", icon: User },
    { num: 3, title: "Residency", icon: MapPin },
    { num: 4, title: "Employment", icon: Briefcase },
    { num: 5, title: "Guarantors", icon: Users },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-green-200">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-green-700 mb-2">Registration Complete!</h2>
            <p className="text-text-muted mb-6">
              Your account has been created and your tenant application has been submitted for review.
            </p>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-6">
              <p className="text-sm text-amber-700">
                <strong>What happens next?</strong><br />
                The administrator will review your application. Once approved, you will be able to log in with your username and password.
              </p>
            </div>
            <Button 
              onClick={() => navigate("/login")}
              className="w-full bg-primary hover:bg-primary-800"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 py-8 px-4" data-testid="register-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary">WilyRash Homes</h1>
          </div>
          <h2 className="text-xl font-semibold text-stone-700">New Tenant Registration</h2>
          <p className="text-sm text-text-muted mt-2">Complete your registration and tenant application</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8 overflow-x-auto pb-2">
          <div className="flex items-center gap-1 md:gap-2">
            {steps.map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <button
                  onClick={() => currentStep > step.num && setCurrentStep(step.num)}
                  className={`flex items-center gap-1 md:gap-2 px-2 md:px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                    currentStep === step.num 
                      ? 'bg-primary text-white' 
                      : currentStep > step.num 
                        ? 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200' 
                        : 'bg-stone-200 text-stone-500'
                  }`}
                  disabled={currentStep < step.num}
                >
                  <step.icon className="w-4 h-4" />
                  <span className="hidden md:inline text-sm font-medium">{step.title}</span>
                  <span className="md:hidden text-sm font-medium">{step.num}</span>
                </button>
                {idx < steps.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-stone-400 mx-1" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <Card className="border-stone-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {(() => {
                const StepIcon = steps[currentStep - 1].icon;
                return StepIcon ? <StepIcon className="w-5 h-5 text-primary" /> : null;
              })()}
              Step {currentStep}: {steps[currentStep - 1].title}
            </CardTitle>
            <CardDescription>
              {currentStep === 1 && "Create your login credentials"}
              {currentStep === 2 && "Enter your personal information"}
              {currentStep === 3 && "Provide your residency history (optional but helpful)"}
              {currentStep === 4 && "Tell us about your occupation"}
              {currentStep === 5 && "Provide two guarantors who can vouch for you"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Account Info */}
            {currentStep === 1 && (
              <div className="space-y-4">
                {/* Profile Photo */}
                <div className="flex justify-center mb-4">
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="relative w-24 h-24 rounded-full bg-stone-100 border-2 border-dashed border-stone-300 hover:border-primary cursor-pointer transition-colors overflow-hidden group"
                    data-testid="register-photo-upload"
                  >
                    {photoPreview ? (
                      <img src={photoPreview} alt="Profile Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <User className="w-8 h-8 text-stone-400" />
                        <span className="text-xs text-stone-500 mt-1">Add Photo *</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                </div>
                <p className="text-xs text-center text-text-muted -mt-2 mb-2">Profile photo is required *</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Username *</Label>
                    <Input
                      name="username"
                      placeholder="Choose a username"
                      value={accountData.username}
                      onChange={handleAccountChange}
                      data-testid="register-username-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email (Optional)</Label>
                    <Input
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={accountData.email}
                      onChange={handleAccountChange}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Full Legal Name (Surname First) *</Label>
                  <Input
                    name="full_name"
                    placeholder="e.g., Okonkwo John Chukwuemeka"
                    value={accountData.full_name}
                    onChange={handleAccountChange}
                    data-testid="register-fullname-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Telephone Number *</Label>
                  <Input
                    name="telephone"
                    type="tel"
                    placeholder="e.g., +234 801 234 5678"
                    value={accountData.telephone}
                    onChange={handleAccountChange}
                    data-testid="register-telephone-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <div className="relative">
                      <Input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Min. 6 characters"
                        value={accountData.password}
                        onChange={handleAccountChange}
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm Password *</Label>
                    <Input
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm password"
                      value={accountData.confirmPassword}
                      onChange={handleAccountChange}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Personal Info */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Age *</Label>
                    <Input
                      type="number"
                      placeholder="Your age"
                      value={applicationData.age}
                      onChange={(e) => handleAppChange("age", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Gender *</Label>
                    <Select value={applicationData.gender} onValueChange={(v) => handleAppChange("gender", v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Contact Info Section */}
                <div className="p-4 bg-stone-50 rounded-xl space-y-4">
                  <h3 className="font-semibold text-primary text-sm">
                    Contact Info (at least one required)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Mobile Phone #</Label>
                      <Input
                        placeholder="e.g., +234 801 234 5678"
                        value={applicationData.mobile_phone}
                        onChange={(e) => handleAppChange("mobile_phone", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>WhatsApp # (preferred)</Label>
                      <Input
                        placeholder="e.g., +234 801 234 5678"
                        value={applicationData.whatsapp_phone}
                        onChange={(e) => handleAppChange("whatsapp_phone", e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-stone-500">
                    WhatsApp is preferred for faster communication
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Marital Status *</Label>
                  <Select value={applicationData.marital_status} onValueChange={(v) => handleAppChange("marital_status", v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {applicationData.marital_status === "Other" && (
                  <div className="space-y-2">
                    <Label>Please specify</Label>
                    <Input
                      value={applicationData.marital_status_other}
                      onChange={(e) => handleAppChange("marital_status_other", e.target.value)}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>National ID Number (NIN) *</Label>
                    <Input
                      placeholder="Enter NIN"
                      value={applicationData.national_id}
                      onChange={(e) => handleAppChange("national_id", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Driver's License (Optional)</Label>
                    <Input
                      placeholder="Enter license number"
                      value={applicationData.driver_license}
                      onChange={(e) => handleAppChange("driver_license", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Residency History */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Last Address 1</Label>
                  <Textarea
                    placeholder="Enter your previous address"
                    value={applicationData.last_address_1}
                    onChange={(e) => handleAppChange("last_address_1", e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Address 2</Label>
                  <Textarea
                    placeholder="Another previous address (if applicable)"
                    value={applicationData.last_address_2}
                    onChange={(e) => handleAppChange("last_address_2", e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Tenant Address</Label>
                  <Textarea
                    placeholder="Address where you were a tenant"
                    value={applicationData.last_tenant_address}
                    onChange={(e) => handleAppChange("last_tenant_address", e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Duration at Last Location</Label>
                    <Input
                      placeholder="e.g., 2 years"
                      value={applicationData.duration_at_last_location}
                      onChange={(e) => handleAppChange("duration_at_last_location", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Past Landlord's Phone</Label>
                    <Input
                      placeholder="Enter phone number"
                      value={applicationData.past_landlord_phone}
                      onChange={(e) => handleAppChange("past_landlord_phone", e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reason for Leaving</Label>
                  <Textarea
                    placeholder="Why did you leave your last residence?"
                    value={applicationData.reason_for_leaving}
                    onChange={(e) => handleAppChange("reason_for_leaving", e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Can your past landlord be contacted?</Label>
                  <Select value={applicationData.can_landlord_be_contacted} onValueChange={(v) => handleAppChange("can_landlord_be_contacted", v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 4: Employment */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Occupation *</Label>
                  <Select value={applicationData.occupation} onValueChange={(v) => handleAppChange("occupation", v)}>
                    <SelectTrigger><SelectValue placeholder="Select occupation" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Employed">Employed</SelectItem>
                      <SelectItem value="Self-Employed">Self-Employed</SelectItem>
                      <SelectItem value="Student">Student</SelectItem>
                      <SelectItem value="Retired">Retired</SelectItem>
                      <SelectItem value="Unemployed">Unemployed</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {applicationData.occupation === "Other" && (
                  <div className="space-y-2">
                    <Label>Please specify</Label>
                    <Input
                      value={applicationData.occupation_other}
                      onChange={(e) => handleAppChange("occupation_other", e.target.value)}
                    />
                  </div>
                )}

                {(applicationData.occupation === "Employed" || applicationData.occupation === "Self-Employed") && (
                  <>
                    <div className="space-y-2">
                      <Label>Duration of Employment</Label>
                      <Input
                        placeholder="e.g., 2 years"
                        value={applicationData.employment_duration}
                        onChange={(e) => handleAppChange("employment_duration", e.target.value)}
                      />
                    </div>
                    {applicationData.occupation === "Employed" && (
                      <div className="space-y-2">
                        <Label>Monthly Salary (₦)</Label>
                        <Input
                          placeholder="Enter monthly salary"
                          value={applicationData.monthly_salary}
                          onChange={(e) => handleAppChange("monthly_salary", e.target.value)}
                        />
                      </div>
                    )}
                    {applicationData.occupation === "Self-Employed" && (
                      <div className="space-y-2">
                        <Label>Estimated Monthly Returns (₦)</Label>
                        <Input
                          placeholder="Enter estimated returns"
                          value={applicationData.self_employed_returns}
                          onChange={(e) => handleAppChange("self_employed_returns", e.target.value)}
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="space-y-2">
                  <Label>Additional Occupants *</Label>
                  <Select value={applicationData.additional_occupants} onValueChange={(v) => handleAppChange("additional_occupants", v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="None">None - Living alone</SelectItem>
                      <SelectItem value="1">1 additional person</SelectItem>
                      <SelectItem value="2">2 additional people</SelectItem>
                      <SelectItem value="3">3 additional people</SelectItem>
                      <SelectItem value="4+">4 or more people</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>How long do you plan to rent? *</Label>
                  <Select value={applicationData.rental_duration} onValueChange={(v) => handleAppChange("rental_duration", v)}>
                    <SelectTrigger><SelectValue placeholder="Select duration" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6 months">6 months</SelectItem>
                      <SelectItem value="1 year">1 year</SelectItem>
                      <SelectItem value="2 years">2 years</SelectItem>
                      <SelectItem value="3+ years">3+ years</SelectItem>
                      <SelectItem value="Undecided">Undecided</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 5: Guarantors */}
            {currentStep === 5 && (
              <div className="space-y-6">
                {/* Guarantor 1 */}
                <div className="p-4 bg-stone-50 rounded-xl space-y-4">
                  <h3 className="font-semibold text-primary flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Guarantor 1
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        placeholder="Enter guarantor's name"
                        value={applicationData.guarantor_1_name}
                        onChange={(e) => handleAppChange("guarantor_1_name", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number *</Label>
                      <Input
                        placeholder="Enter phone number"
                        value={applicationData.guarantor_1_phone}
                        onChange={(e) => handleAppChange("guarantor_1_phone", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Address *</Label>
                    <Textarea
                      placeholder="Enter guarantor's address"
                      value={applicationData.guarantor_1_address}
                      onChange={(e) => handleAppChange("guarantor_1_address", e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Can this guarantor be contacted? *</Label>
                    <Select value={applicationData.guarantor_1_can_contact} onValueChange={(v) => handleAppChange("guarantor_1_can_contact", v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Guarantor 2 */}
                <div className="p-4 bg-stone-50 rounded-xl space-y-4">
                  <h3 className="font-semibold text-primary flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Guarantor 2
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        placeholder="Enter guarantor's name"
                        value={applicationData.guarantor_2_name}
                        onChange={(e) => handleAppChange("guarantor_2_name", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number *</Label>
                      <Input
                        placeholder="Enter phone number"
                        value={applicationData.guarantor_2_phone}
                        onChange={(e) => handleAppChange("guarantor_2_phone", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Address *</Label>
                    <Textarea
                      placeholder="Enter guarantor's address"
                      value={applicationData.guarantor_2_address}
                      onChange={(e) => handleAppChange("guarantor_2_address", e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Can this guarantor be contacted? *</Label>
                    <Select value={applicationData.guarantor_2_can_contact} onValueChange={(v) => handleAppChange("guarantor_2_can_contact", v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(prev => prev - 1)}
                disabled={currentStep === 1}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>

              {currentStep < 5 ? (
                <Button
                  onClick={handleNext}
                  className="bg-primary hover:bg-primary-800"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? "Submitting..." : "Submit Registration"}
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Login Link */}
        <p className="text-center text-sm text-stone-500 mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline">
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
}
